/**
 * SubmissionFilters Component
 *
 * Renders the complete filter bar for submissions including:
 * - Name and email text filters
 * - Launch selector
 * - Date range pickers
 * - Clear filters button
 *
 * Follows Single Responsibility Principle - only handles filter UI
 */

import { format } from "date-fns";
import { User, Mail, Calendar, Rocket } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { SubmissionFilters as FiltersType, LaunchWithCodes } from "../store/types";

interface SubmissionFiltersProps {
  /** Current filter values */
  filters: FiltersType;
  /** Available launches for filtering */
  launches: LaunchWithCodes[] | undefined;
  /** Currently selected launch ID */
  selectedLaunchId: string;
  /** Callback when a filter changes */
  onFilterChange: (key: keyof FiltersType, value: string) => void;
  /** Callback when date filter changes */
  onDateChange: (key: "dateFrom" | "dateTo", date: Date | undefined) => void;
  /** Callback when launch selection changes */
  onLaunchChange: (launchId: string) => void;
  /** Callback to clear all filters */
  onClearFilters: () => void;
  /** Whether any filters are active */
  hasActiveFilters: boolean;
}

/**
 * Filter bar component for submissions list
 *
 * @example
 * ```tsx
 * <SubmissionFilters
 *   filters={filters}
 *   launches={launches}
 *   selectedLaunchId={selectedLaunchId}
 *   onFilterChange={setFilter}
 *   onDateChange={setDateFilter}
 *   onLaunchChange={setSelectedLaunchId}
 *   onClearFilters={clearFilters}
 *   hasActiveFilters={hasActiveFilters}
 * />
 * ```
 */
export function SubmissionFilters({
  filters,
  launches,
  selectedLaunchId,
  onFilterChange,
  onDateChange,
  onLaunchChange,
  onClearFilters,
  hasActiveFilters,
}: SubmissionFiltersProps) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      {/* Text filters row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="relative">
          <User
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={20}
            aria-hidden="true"
          />
          <Input
            placeholder="Filtrar por nombre"
            value={filters.name}
            onChange={(e) => onFilterChange("name", e.target.value)}
            className="pl-10 w-full border-gray-300 focus:border-primary focus:ring-primary"
            aria-label="Filtrar submissions por nombre"
          />
        </div>
        <div className="relative">
          <Mail
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={20}
            aria-hidden="true"
          />
          <Input
            placeholder="Filtrar por email"
            value={filters.email}
            onChange={(e) => onFilterChange("email", e.target.value)}
            className="pl-10 w-full border-gray-300 focus:border-primary focus:ring-primary"
            aria-label="Filtrar submissions por email"
          />
        </div>
      </div>

      {/* Launch and date filters row */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Launch filter */}
        <Select
          value={selectedLaunchId || "__all__"}
          onValueChange={(value) =>
            onLaunchChange(value === "__all__" ? "" : value)
          }
        >
          <SelectTrigger
            className="w-[250px]"
            aria-label="Filtrar por lanzamiento"
          >
            <Rocket className="mr-2 h-4 w-4 text-muted-foreground" />
            <SelectValue placeholder="Filtrar por lanzamiento" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los lanzamientos</SelectItem>
            {launches?.map((launch) => (
              <SelectItem key={launch.id} value={String(launch.id)}>
                {launch.client_name} -{" "}
                {launch.form_title || `Form ${launch.jotform_form_id}`} (
                {launch.submission_count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Date from filter */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-normal",
                !filters.dateFrom && "text-muted-foreground"
              )}
              aria-label="Seleccionar fecha desde"
            >
              <Calendar className="mr-2 h-4 w-4" aria-hidden="true" />
              {filters.dateFrom
                ? format(new Date(filters.dateFrom), "dd/MM/yyyy")
                : "Fecha desde"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={
                filters.dateFrom ? new Date(filters.dateFrom) : undefined
              }
              onSelect={(date) => onDateChange("dateFrom", date)}
              initialFocus
              className="pointer-events-auto"
            />
          </PopoverContent>
        </Popover>

        {/* Date to filter */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-normal",
                !filters.dateTo && "text-muted-foreground"
              )}
              aria-label="Seleccionar fecha hasta"
            >
              <Calendar className="mr-2 h-4 w-4" aria-hidden="true" />
              {filters.dateTo
                ? format(new Date(filters.dateTo), "dd/MM/yyyy")
                : "Fecha hasta"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={filters.dateTo ? new Date(filters.dateTo) : undefined}
              onSelect={(date) => onDateChange("dateTo", date)}
              initialFocus
              className="pointer-events-auto"
            />
          </PopoverContent>
        </Popover>

        {/* Clear filters button */}
        <Button
          variant="outline"
          onClick={onClearFilters}
          className={cn(
            "text-gray-600 hover:bg-gray-50",
            hasActiveFilters && "border-primary text-primary"
          )}
        >
          Limpiar filtros
        </Button>
      </div>
    </div>
  );
}
